import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { AD_BANNERS, AdBannerDef, PromoControls } from './adBanners';
import { ActiveAdBanner } from '@radevents/shared';
import { getActiveAdBanners } from '../services/adBannerService';
import './PromoBannerSlot.css';

export type { PromoControls } from './adBanners';

/**
 * Der eine Werbeplatz unter „n EVENTS" (`docs/werbung-spezifikation.md`, Abschnitt 2). Welche
 * Anzeigen darin laufen, schaltet die Redaktion unter `/admin/werbeplatz`; laufen mehrere,
 * wechseln sie sich alle paar Sekunden ab, statt dass eine die andere verdrängt.
 *
 * Drei Dinge, die ein Karussell sonst kaputtmacht:
 *
 * - **Der Startbanner wird gewürfelt.** Wer nach fünf Sekunden weiterzieht, sieht nur den ersten;
 *   bei fester Reihenfolge sammelte derselbe Slot alle Einblendungen der Abspringer ein und die
 *   Klickraten wären nicht mehr vergleichbar. **Ausnahme Ankündigung** (`kind = ANNOUNCEMENT`,
 *   eingestellt unter `/admin/werbeplatz`): sie steht vorn und kommt zuerst, die Werbung folgt im
 *   Wechsel. Das ist gewollt, die Ankündigung soll jeder sehen.
 * - **Es läuft nur, wenn niemand liest.** Überfahren, Tastaturfokus im Banner, Tab im
 *   Hintergrund und `prefers-reduced-motion` halten den Wechsel an. Wer selbst blättert, hält
 *   ihn dauerhaft an: ein Pfeil oder ein Punkt schaltet den Selbstlauf ab, sonst wechselte das
 *   Bild gleich wieder weg (und WCAG 2.2.2 verlangt für bewegte Inhalte ohnehin eine solche
 *   Möglichkeit).
 * - **Je Banner wird nur der sichtbare Zustand gezeichnet.** Nur die aktive Anzeige hängt im DOM,
 *   deshalb meldet jede ihre Einblendung erst, wenn sie tatsächlich zu sehen war. `trackOnce`
 *   sorgt dafür, dass eine zweite Runde nicht doppelt zählt.
 *
 * Pfeile und Punkte liegen **im** Banner: unter der Karte standen sie auf dem hellen Grund der
 * Liste und sahen aus wie ein vergessenes Element. Auf dem Handy gibt es **keine Pfeile** —
 * dort wird gewischt, und eine eigene Knopfzeile hätte die Karte um vierzig Pixel verlängert.
 *
 * `activeKeys` setzt die laufenden Anzeigen von außen (Design-Lab, Vorschau in der Verwaltung);
 * ohne fragt der Slot `/public/ad-banners`. Bis die Antwort da ist und wenn sie scheitert, bleibt
 * der Platz leer: eine abgeschaltete Anzeige darf auch bei einem Fehler nicht erscheinen.
 */
export default function PromoBannerSlot({ activeKeys, startKey }: {
  /** Von außen gesetzt (Design-Lab, Vorschau); ein bloßer Schlüssel gilt als Werbung. */
  activeKeys?: (string | ActiveAdBanner)[];
  /** Nur Design-Lab: mit dieser Anzeige beginnen statt gewürfelt. */
  startKey?: string;
}) {
  const fetched = useActiveAdBanners(activeKeys === undefined);
  const active = activeKeys ? activeKeys.map(asActive) : fetched;
  // Als Zeichenkette, damit eine neue, gleiche Antwort keinen neuen Wechsel startet.
  const announced = (active ?? []).filter(a => a.kind === 'ANNOUNCEMENT').map(a => a.key).join('|');
  const ads = (active ?? []).filter(a => a.kind !== 'ANNOUNCEMENT').map(a => a.key).join('|');
  const { banners, leading } = useMemo(() => {
    const pick = (keys: string) => AD_BANNERS.filter(b => keys.split('|').includes(b.key));
    const first = pick(announced);
    return { banners: [...first, ...pick(ads)], leading: first.length };
  }, [announced, ads]);
  if (banners.length === 0) return null;
  // `key` aus der Auswahl: ändert sie sich, beginnt der Wechsel von vorn.
  return <PromoRotation key={banners.map(b => b.key).join('|')} banners={banners} leading={leading} startKey={startKey} />;
}

/** Der Werbeplatz zeigt gerade eine Ankündigung (und die Werbung im Wechsel dahinter). */
export function hasActiveAnnouncement(active: ActiveAdBanner[] | null): boolean {
  return (active ?? []).some(a => a.kind === 'ANNOUNCEMENT');
}

/**
 * Einmal je Seitenaufruf gefragt und für weitere Besuche der Liste im Modul gehalten: zurück aus
 * einem Event soll der Werbeplatz nicht erst leer stehen und dann aufspringen.
 */
let cachedKeys: ActiveAdBanner[] | null = null;
let pending: Promise<ActiveAdBanner[]> | null = null;

const asActive = (a: string | ActiveAdBanner): ActiveAdBanner => (typeof a === 'string' ? { key: a, kind: 'AD' } : a);

/** Die laufenden Anzeigen mit ihrer Art; `null`, solange die Antwort aussteht. */
export function useActiveAdBanners(enabled = true): ActiveAdBanner[] | null {
  const [keys, setKeys] = useState<ActiveAdBanner[] | null>(cachedKeys);
  useEffect(() => {
    if (!enabled || cachedKeys) return;
    let alive = true;
    pending = pending ?? getActiveAdBanners();
    pending
      .then(result => { cachedKeys = result; if (alive) setKeys(result); })
      .catch(() => { pending = null; if (alive) setKeys([]); });
    return () => { alive = false; };
  }, [enabled]);
  return keys;
}

/** Nur für Tests: den Modul-Cache zwischen zwei Fällen leeren. */
export function resetAdBannerCacheForTests() {
  cachedKeys = null;
  pending = null;
}

const INTERVAL_MS = 7000;

/** Reagiert auf die Systemeinstellung, auch wenn sie während des Besuchs umgestellt wird. */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

function PromoRotation({ banners, leading = 0, startKey }: {
  banners: AdBannerDef[];
  /** So viele Ankündigungen stehen vorn; der Start wird nur unter ihnen gewürfelt. */
  leading?: number;
  startKey?: string;
}) {
  const count = banners.length;
  const [index, setIndex] = useState(() => {
    const fixed = startKey ? banners.findIndex(b => b.key === startKey) : -1;
    return fixed >= 0 ? fixed : Math.floor(Math.random() * (leading > 0 ? leading : count));
  });
  const [stopped, setStopped] = useState(false);
  const [resting, setResting] = useState(false);
  const [tabHidden, setTabHidden] = useState(() => typeof document !== 'undefined' && document.hidden);
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const onVisibility = () => setTabHidden(document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const running = count > 1 && !stopped && !resting && !tabHidden && !reducedMotion;

  useEffect(() => {
    if (!running) return;
    const timer = window.setTimeout(() => setIndex(i => (i + 1) % count), INTERVAL_MS);
    return () => window.clearTimeout(timer);
    // `index` gehört dazu: jeder Wechsel startet die Zeit neu.
  }, [running, index, count]);

  /** Von Hand geblättert: hinschalten und den Selbstlauf beenden. */
  const goTo = useCallback((next: number) => {
    setIndex((next + count) % count);
    setStopped(true);
  }, [count]);

  /**
   * Desktop: Pfeile an den Rändern, Punkte mittig unten. Handy: nur die Punkte, ebenfalls unten
   * in der Karte — geblättert wird dort mit dem Daumen. Läuft nur eine Anzeige, gibt es nichts
   * zu blättern, und die Anzeige bekommt keine Bedienelemente.
   */
  const controls = useCallback<PromoControls>((variant) => {
    const arrow = (dir: -1 | 1) => (
      <button
        type="button"
        className={`promo-slot-arrow promo-slot-arrow--${dir < 0 ? 'prev' : 'next'}`}
        onClick={() => goTo(index + dir)}
        aria-label={dir < 0 ? 'Vorherige Anzeige' : 'Nächste Anzeige'}
      >
        {dir < 0 ? <ChevronLeft size={16} aria-hidden /> : <ChevronRight size={16} aria-hidden />}
      </button>
    );
    const dots = banners.map((banner, i) => (
      <button
        key={banner.key}
        type="button"
        className={`promo-slot-dot${i === index ? ' promo-slot-dot--on' : ''}`}
        onClick={() => goTo(i)}
        aria-label={`Anzeige „${banner.label}" zeigen`}
        aria-current={i === index ? 'true' : undefined}
      />
    ));

    const punkte = <div className="promo-slot-dots promo-slot-dots--floating">{dots}</div>;
    if (variant === 'mobile') return punkte;
    return <>{arrow(-1)}{arrow(1)}{punkte}</>;
  }, [banners, index, goTo]);

  /**
   * Wischen auf dem Touchscreen. 40px Schwelle und „waagerecht muss deutlicher sein als
   * senkrecht", sonst blättert die Anzeige schon beim Scrollen durch die Liste weiter.
   */
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.changedTouches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const from = touchStart.current;
    touchStart.current = null;
    if (!from || count < 2) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - from.x;
    if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(t.clientY - from.y)) return;
    goTo(index + (dx < 0 ? 1 : -1));
  };

  const current = banners[index];

  return (
    <section
      aria-label={count > 1 ? 'Anzeigen' : 'Anzeige'}
      className={`promo-slot${reducedMotion || count < 2 ? '' : ' promo-slot-fade'}`}
      key={current.key}
      onMouseEnter={() => setResting(true)}
      onMouseLeave={() => setResting(false)}
      onFocus={() => setResting(true)}
      onBlur={() => setResting(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {current.render(count > 1 ? controls : undefined)}
    </section>
  );
}
